import { useEffect, useState } from 'react';
import { PageHero } from '@/components/PageHero';
import { GalleryCard } from '@/components/GalleryCard';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { SEO } from '@/components/SEO';
import { FilmStripGallery } from '@/components/FilmStripGallery';
import { SectionDivider } from '@/components/SectionDivider';
import { Skeleton } from '@/components/ui/skeleton';
import { getGalleries } from '@/lib/api';
import { SITE_IMAGES, SITE_VIDEOS } from '@/lib/siteImages';
import { GALLERIES_PAGE } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';

export default function Galleries() {
  const [galleries, setGalleries] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getGalleries()
      .then(setGalleries)
      .catch(() => setError(true));
  }, []);

  const filmStripItems = (galleries || []).map((cat) => ({
    slug: cat.slug,
    to: `/galleries/${cat.slug}`,
    image: cat.cover_image_url,
    title: cat.name,
  }));

  return (
    <div data-testid={GALLERIES_PAGE.root}>
      <SEO
        title="Galleries"
        description="Browse LXRA Studios' photography galleries — portraits, events, graduations, automotive, and couples sessions across the DMV."
      />
      <PageHero
        kicker="Explore My Work"
        title="GALLERIES"
        subtitle="Every moment has a story. Here are some of my favorite captured moments."
        backgroundImage={SITE_IMAGES.galleriesHero}
        backgroundVideo={SITE_VIDEOS.galleriesHero}
      />

      <FilmStripGallery items={filmStripItems} />

      <SectionDivider />

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          {error && (
            <p className="text-sm text-[hsl(var(--lxra-muted))]">Unable to load galleries right now. Please try again shortly.</p>
          )}

          {!galleries && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid={GALLERIES_PAGE.grid}>
              <SkeletonList count={5} className="aspect-[3/4] rounded-2xl bg-[#141416]" />
            </div>
          )}

          {galleries && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid={GALLERIES_PAGE.grid}>
              {galleries.map((cat, i) => (
                <Reveal key={cat.slug} delay={i * 0.05}>
                  <GalleryCard category={cat} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection testId="galleries-cta-banner" buttonTestId="galleries-cta-book-now-button" />
    </div>
  );
}
